import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const GSI_SRC = "https://accounts.google.com/gsi/client";
let gsiLoading = null;

function loadGsi() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!gsiLoading) {
    gsiLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = GSI_SRC;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => { gsiLoading = null; reject(new Error("Couldn't load Google sign-in")); };
      document.head.appendChild(s);
    });
  }
  return gsiLoading;
}

/**
 * Google's official "Sign in / Sign up with Google" button (Google Identity Services).
 * Renders nothing unless the backend has GOOGLE_CLIENT_ID set. onCredential gets the ID token.
 */
export default function GoogleSignInButton({ mode = "signin", onCredential, onReady }) {
  const ref = useRef(null);
  const cb = useRef(onCredential);
  cb.current = onCredential;
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/google/config");
        if (!data.enabled || !alive) return;
        await loadGsi();
        if (!alive || !ref.current) return;
        window.google.accounts.id.initialize({
          client_id: data.client_id,
          callback: (resp) => cb.current?.(resp.credential),
          ux_mode: "popup",
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        const width = Math.min(ref.current.offsetWidth || 320, 400); // Google caps the button at 400px
        window.google.accounts.id.renderButton(ref.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "pill",
          text: mode === "signup" ? "signup_with" : "continue_with",
          logo_alignment: "center",
          width,
        });
        setEnabled(true);
        onReady?.(true);
      } catch {
        /* Google sign-in not configured or blocked — the email form still works */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [mode]);

  return <div ref={ref} className={enabled ? "flex justify-center min-h-[44px]" : "hidden"} data-testid="google-signin" />;
}
